import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  ChevronRight,
  Gift,
  LifeBuoy,
  Megaphone,
  Plug,
  Share2,
  Store,
  Target,
  Ticket,
  Truck,
  UsersRound,
} from 'lucide-react';
import { api } from '@/lib/api';
import { canOpenPath, limitedAccess } from '@/lib/access';
import { getTgUser, getWebApp, haptic } from '@/lib/telegram';
import { RowGroup, RowLink } from '@/components/ui/row-link';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';

/**
 * Вкладка «Ещё»: всё, что не попало в четыре основных раздела. Сверху строка профиля
 * (имя, аккаунт, язык и поддержка живут там), ниже продвижение и служебные разделы.
 * Деньги (выводы, выводы клиентов, статистика) во вкладке «Деньги», клиенты и подписки
 * во вкладке «Клиенты».
 */
export const Route = createFileRoute('/more')({ component: More });

function More() {
  const { t } = useTranslation();

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const { data: partner, isPending } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });
  const isDelegate = partner?.isDelegate === true;
  // Менеджер или свой набор: показываем только разделы, которые ему открыл владелец.
  const access = limitedAccess(partner);
  const can = (path: string) => canOpenPath(access, path);

  if (isPending && !partner) return <MoreSkeleton />;

  const tgUser = getTgUser();
  const name =
    (tgUser ? [tgUser.first_name, tgUser.last_name].filter(Boolean).join(' ') : partner?.firstName) ||
    t('Tg user fallback');
  const username = tgUser?.username ?? partner?.username ?? null;
  const photo = tgUser?.photo_url ?? null;
  const initials = (name.match(/[\p{L}\p{N}]/gu) ?? ['?']).slice(0, 2).join('').toUpperCase();

  return (
    <div className="p-4 pb-6 space-y-5">
      <ProfileRow enabled={access === null}>
        {photo ? (
          <img src={photo} alt="" className="size-12 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="grid size-12 shrink-0 place-items-center rounded-full bg-white/[0.08] font-semibold">
            {initials}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[16px] font-semibold">{name}</span>
          <span className="block truncate text-[13px] text-dim">
            {username ? `@${username} · ` : ''}
            {isDelegate
              ? partner?.delegateRole === 'manager'
                ? 'менеджер кабинета'
                : partner?.delegateRole === 'custom'
                  ? 'доступ выбран владельцем'
                  : 'администратор кабинета'
              : 'профиль и настройки'}
          </span>
        </span>
        {access === null && <ChevronRight className="size-[18px] shrink-0 text-dim" />}
      </ProfileRow>

      {(['/marketing', '/campaigns', '/vouchers', '/giveaways', '/referrals', '/mirrors'] as const).some(can) && (
      <Group title="Продвижение">
        {can('/marketing') && <RowLink to="/marketing" icon={<Megaphone />} label="Рассылки" hint="Сообщение всем клиентам бота" />}
        {can('/campaigns') && <RowLink to="/campaigns" icon={<Target />} label="Реклама" hint="Ссылки с метками и окупаемость" />}
        {can('/vouchers') && <RowLink to="/vouchers" icon={<Ticket />} label="Ваучеры" hint="Подписки кодами для блогеров и акций" />}
        {can('/giveaways') && <RowLink to="/giveaways" icon={<Gift />} label="Розыгрыши" hint="Подписки в подарок среди клиентов" />}
        {can('/referrals') && <RowLink to="/referrals" icon={<Share2 />} label={t('Referrals')} hint="Бонус клиентам за приглашённых" />}
        {can('/mirrors') && <RowLink to="/mirrors" icon={<Store />} label="Свой VPN у клиентов" hint="Клиенты открывают свои боты" />}
      </Group>
      )}

      <Group title="Кабинет">
        {!isDelegate && <RowLink to="/team" icon={<UsersRound />} label="Команда" hint="Помощники с доступом" />}
        {!isDelegate && (
          <RowLink to="/vpn-api" icon={<Plug />} label="VPN API" hint="Подписки из вашего сайта или биллинга" />
        )}
        {can('/bot-import') && (
          <RowLink to="/bot-import" icon={<Truck />} label="Переезд бота" hint="Если бот с клиентами уже есть на другой платформе" />
        )}
        <RowLink to="/status" icon={<Activity />} label="Статус сети" hint="Серверы и доступность" />
        <RowLink
          icon={<LifeBuoy />}
          label={t('Support')}
          hint="Ответит человек"
          onClick={() => getWebApp()?.openTelegramLink('https://t.me/hex_support')}
        />
      </Group>
    </div>
  );
}

/** Строка профиля: ссылка у владельца и полного администратора, просто карточка у ограниченного
 * делегата (профиль владельца ему закрыт). */
function ProfileRow({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  const cls = 'surface-card flex items-center gap-3 rounded-[20px] p-3.5';
  if (!enabled) return <div className={cls}>{children}</div>;
  return (
    <Link
      to="/profile"
      onClick={() => haptic('light')}
      className={`${cls} active:scale-[0.99] transition-transform`}
    >
      {children}
    </Link>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 px-1 text-[12.5px] text-dim">{title}</h2>
      <RowGroup>{children}</RowGroup>
    </section>
  );
}

function MoreSkeleton() {
  return (
    <SkeletonPage className="p-4 pb-6 space-y-5">
      <SkeletonCard className="flex items-center gap-3 rounded-[20px] p-3.5">
        <Skeleton className="size-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <SkeletonLine className="w-[50%]" />
          <SkeletonLine size="sm" className="w-[35%]" />
        </div>
      </SkeletonCard>
      {[6, 5].map((n, i) => (
        <section key={i}>
          <SkeletonLine size="sm" className="mb-2 ml-1 w-24" />
          <SkeletonCard className="rounded-[20px]" style={{ minHeight: n * 61 }} />
        </section>
      ))}
    </SkeletonPage>
  );
}
